import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

function EyeIcon({ open }) {
  return open ? (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
      <circle cx="12" cy="12" r="3"/>
    </svg>
  ) : (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/>
      <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  );
}

function ResetPasswordPage() {
  const navigate = useNavigate();
  const { updatePassword } = useAuth();
  const [password, setPassword] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirmPw) { setError('Passwörter stimmen nicht überein.'); return; }
    if (password.length < 8) { setError('Passwort muss mindestens 8 Zeichen haben.'); return; }
    setLoading(true);
    const { error: err } = await updatePassword(password);
    setLoading(false);
    if (err) {
      setError(err.message);
    } else {
      setDone(true);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.logo} onClick={() => navigate('/')}>
          <img src="/logo-frontup.png" style={{width:32,height:32,borderRadius:8,objectFit:'cover'}} alt="FrontUp" />
          <span style={styles.logoName}>FrontUp</span>
        </div>

        {done ? (
          <>
            <div style={styles.successIcon}>
              <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="#fff" strokeWidth="2.5">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            </div>
            <h1 style={{...styles.title, marginTop: 20}}>Passwort geändert!</h1>
            <p style={{...styles.sub, marginBottom: 28}}>Dein Passwort wurde erfolgreich aktualisiert.</p>
            <button onClick={() => navigate('/dashboard')} style={styles.submitBtn}>Zum Dashboard</button>
          </>
        ) : (
          <>
            <h1 style={styles.title}>Neues Passwort</h1>
            <p style={styles.sub}>Gib dein neues Passwort ein.</p>

            <form onSubmit={handleSubmit} style={styles.form}>
              <div style={styles.field}>
                <label style={styles.label}>Neues Passwort</label>
                <div style={styles.pwWrap}>
                  <input
                    type={showPw ? 'text' : 'password'}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    style={{...styles.input, paddingRight: 44}}
                  />
                  <button type="button" onClick={() => setShowPw(v => !v)} style={styles.eyeBtn}>
                    <EyeIcon open={showPw} />
                  </button>
                </div>
              </div>

              <div style={styles.field}>
                <label style={styles.label}>Passwort bestätigen</label>
                <div style={styles.pwWrap}>
                  <input
                    type={showConfirm ? 'text' : 'password'}
                    value={confirmPw}
                    onChange={e => setConfirmPw(e.target.value)}
                    placeholder="••••••••"
                    required
                    style={{...styles.input, paddingRight: 44}}
                  />
                  <button type="button" onClick={() => setShowConfirm(v => !v)} style={styles.eyeBtn}>
                    <EyeIcon open={showConfirm} />
                  </button>
                </div>
              </div>

              {error && <div style={styles.error}>{error}</div>}

              <button type="submit" disabled={loading} style={styles.submitBtn}>
                {loading ? 'Speichern...' : 'Passwort speichern'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: '#f7f7f7',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 20px',
  },
  card: {
    background: '#fff',
    border: '1.5px solid #e5e5e5',
    borderRadius: 20,
    padding: '48px 44px',
    width: '100%',
    maxWidth: 440,
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginBottom: 32,
    cursor: 'pointer',
  },
  logoName: {
    fontSize: 18,
    fontWeight: 800,
    letterSpacing: '-.5px',
    color: '#111',
  },
  successIcon: {
    width: 48,
    height: 48,
    background: '#111',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {
    fontSize: 28,
    fontWeight: 800,
    letterSpacing: '-1px',
    color: '#111',
    marginBottom: 6,
  },
  sub: {
    fontSize: 15,
    color: '#888',
    lineHeight: 1.6,
    marginBottom: 32,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: 7,
  },
  label: {
    fontSize: 13,
    fontWeight: 600,
    color: '#111',
  },
  input: {
    padding: '12px 14px',
    border: '1.5px solid #e5e5e5',
    borderRadius: 10,
    fontSize: 15,
    fontFamily: 'inherit',
    color: '#111',
    outline: 'none',
    width: '100%',
    background: '#fff',
    transition: 'border-color .15s',
  },
  pwWrap: {
    position: 'relative',
  },
  eyeBtn: {
    position: 'absolute',
    right: 12,
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#aaa',
    display: 'flex',
    alignItems: 'center',
    padding: 0,
  },
  error: {
    background: '#fff5f5',
    border: '1px solid #fecaca',
    borderRadius: 8,
    padding: '10px 14px',
    fontSize: 13,
    color: '#dc2626',
  },
  submitBtn: {
    padding: '14px',
    background: '#111',
    color: '#fff',
    border: 'none',
    borderRadius: 10,
    fontSize: 15,
    fontWeight: 700,
    fontFamily: 'inherit',
    cursor: 'pointer',
    transition: 'background .15s',
  },
};

export default ResetPasswordPage;
